import moment from 'moment-timezone';
import { SOCKET_URL } from '../../Settings';
import { PackageRouter } from './PackageRouter';

export class SocketHandler {

    api;
    webSocket;
    isOpen = false;
    heartbeatTimer;
    reconnectTimer;
    reconnectDelay = 1000;

    userId;
    playerId;
    gamePlayerId;

    constructor(api) {
        this.api = api;
        this.messageQueue = [];
        this.openSocket();
        this.packageRouter = new PackageRouter(this);
    }

    openSocket = () => {
        console.log('Open Socket.')
        this.stop = false;
        this.makeSocket();
    }

    makeSocket = () => {
        console.log('makeSocket');
        // Phase 0.5 (2026-04-10): WebSocket auth now rides on the
        // sessionid cookie automatically (Channels' AuthMiddlewareStack
        // reads the same cookie Django's HTTP auth does). No token in
        // the query string — it was a JWT-era hack.
        if (!this.api.account || !this.api.account.user) return;
        this.userId = this.api.account.user.id;
        if (this.webSocket && this.webSocket.readyState === WebSocket.CONNECTING) return;
        if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
            console.log('Socket Already Open.');
            this.processQueuedMessages();
            return;
        }

        this.webSocket = new WebSocket(SOCKET_URL);

        this.webSocket.onopen = (event) => {
            console.info('[Sockets] WS open userId=%s queued=%s',
                this.userId, this.messageQueue.length);
            this.isOpen = true;
            // Reconnect (or first connect) succeeded: reset the backoff, drop
            // any pending retry, and clear the "connection lost" menu so the
            // recovery is seamless — no re-login, no stuck modal.
            this.reconnectDelay = 1000;
            if (this.reconnectTimer) {
                clearTimeout(this.reconnectTimer);
                this.reconnectTimer = null;
            }
            if (this.api.parent.connectionMenu) {
                this.api.parent.connectionMenu.handleClose();
            }
            this.api.setIsAuthenticated();
            if (this.messageQueue.length === 0) {
                if (this.api && this.api.parent && this.api.parent.gameController) {
                    this.api.parent.gameController.reconnectToGame();
                }
            } else {
                this.processQueuedMessages();
            }
            // F16 (CC_LOBBY_SAVE_FINISH): fire a minimal WS-open signal so
            // the per-seat re-engage path (GameGroupHandler + GamePage) can
            // re-run the group rendezvous after a reconnect — gameController
            // is null in production, so its reconnectToGame above is a
            // no-op for the live board. `reopen` distinguishes the first
            // open (first-time fetch already covers it) from a reconnect.
            const isReopen = this.hasOpenedOnce === true;
            this.hasOpenedOnce = true;
            window.dispatchEvent(new CustomEvent('ws-open-event',
                { detail: { reopen: isReopen } }));
            // this.startHeartbeat();
        };

        this.webSocket.onmessage = (messageEvent) => {
            this.packageRouter.route(messageEvent)
        };

        this.webSocket.onerror = (event) => {
            console.error('[Sockets] WS error userId=%s', this.userId);
            // Do NOT closeSocket() here: closeSocket arms `stop`, which onclose
            // reads as a DELIBERATE teardown and would suppress the auto-
            // reconnect below. A socket error is transient (server bounce,
            // network blip), not a logout. The browser fires onclose right
            // after onerror; the reconnect decision lives there.
        };

        this.webSocket.onclose = (event) => {
            console.warn('[Sockets] WS close userId=%s code=%s reason=%s wasClean=%s',
                this.userId,
                event && event.code,
                event && event.reason,
                event && event.wasClean);
            this.isOpen = false;
            // `stop` is armed ONLY by closeSocket() — a deliberate teardown
            // (logout / tab-kick). Any other close (a deploy recreating the web
            // container and dropping every live socket, a network drop) is
            // UNEXPECTED, and we auto-reconnect: the sessionid cookie is still
            // valid, so no re-login is needed. makeSocket re-authenticates and
            // onopen re-runs the group rendezvous (ws-open-event /
            // reconnectToGame). Previously both this reconnect and the
            // heartbeat retry were commented out, so a server restart stranded
            // the client on the connection menu until a manual re-login
            // (2026-07-14).
            const wasIntentional = this.stop;
            this.stop = false;
            if (this.api.parent.connectionMenu) this.api.parent.connectionMenu.show();
            this.webSocket = null;
            if (!wasIntentional) {
                this.scheduleReconnect();
            }
        };
    }

    scheduleReconnect = () => {
        // Auto-reconnect with capped exponential backoff after an unexpected
        // close. Guards: never while a deliberate teardown is in flight
        // (stop), never when logged out (no account — a game socket has no
        // business reconnecting a signed-out user), and at most one attempt
        // pending at a time. Each failed attempt's onclose re-schedules with a
        // longer delay; a successful onopen resets reconnectDelay and clears
        // the timer.
        if (this.stop) return;
        if (this.reconnectTimer) return;
        if (!this.api.account || !this.api.account.user) return;
        const delay = this.reconnectDelay || 1000;
        console.info('[Sockets] reconnect scheduled in %sms', delay);
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.reconnectDelay = Math.min(
                (this.reconnectDelay || 1000) * 2, 10000);
            this.makeSocket();
        }, delay);
    }

    closeSocket = () => {
        this.stop = true;
        this.stopHeartbeat();
        // Null-guard: the tab-kicked modal calls closeSocket twice — once
        // on receipt (closes the live socket; onclose nulls this.webSocket)
        // and again when the user clicks "Close this window". Without this
        // guard the second call dereferenced a null this.webSocket and
        // threw, so the modal's onClose never reached setShow(false) and
        // the Close button looked dead.
        if (this.webSocket) {
            this.webSocket.close();
        }
    }


    send = (typeName, packageValue) => {
        let message = this._makePackage(typeName, packageValue);
        if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
            this.webSocket.send(message);
        } else {
            console.log('Socket Still Closed PUSH to queue');
            this.messageQueue.push(message);
            this.makeSocket();
        }
    }

    sendPackagedMessage = (message) => {
        if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
            this.webSocket.send(message);
        } else {
            console.log('Socket Still Closed PUSH to queue');
            this.messageQueue.push(message);
            this.makeSocket();
        }
    }

    _makePackage = (typeName, packageValue) => {
        let message = {};
        message.user = this.userId;
        message.type = typeName;
        message.datetime = moment();
        message.package = packageValue;
        let app = this.api.parent;
        let gameComponent = app.gameComponent;
        if (gameComponent) {
            let gameData = gameComponent.state.gameData;
            if (gameData) {
                message.game = gameData.id;
            }
            let gamePlayerData = gameComponent.state.gamePlayerData;
            if (gamePlayerData) {
                message.game_player = gamePlayerData.id;
            }
        }

        // signature?
        // geoloc
        // ip
        // last_package_id: 0,
        return JSON.stringify(message);
    }

    processQueuedMessages = () => {
        console.log('processQueuedMessages')
        if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) {
            this.messageQueue.forEach(message => this.webSocket.send(message));
            this.messageQueue = [];
        } else {
                if (this.webSocket &&
                this.webSocket.readyState === WebSocket.CONNECTING)
                    setTimeout(this.processQueuedMessages, 1000);
        }
    }

    reconnect = () => {
        if (this.webSocket && this.webSocket.readyState === WebSocket.OPEN) return;
        this.makeSocket();
    }

    startHeartbeat = () => {
        this.lastInterval = getTime();
        this.setHeartbeat();
    }

    stopHeartbeat = () => {
        clearTimeout(this.heartbeatTimer);
    }

    setHeartbeat = () => {
        if (!this.stop) {
            this.heartbeatTimer = setTimeout(
                this.intervalHeartbeat, 1000);
        }
    }

    intervalHeartbeat = () => {
        let now = getTime();
        // let diff = now - this.lastInterval;
        // let offBy = diff - 1000; // 1000 = the 1 second delay I was expecting
        this.lastInterval = now;
        if (!this.stop && this.webSocket.readyState !== WebSocket.OPEN) {
            this.heartbeatAction();
        } else {
            this.setHeartbeat();
        }
        // if(offBy > 100) { // don't trigger on small stutters less than 100ms
        //     let message = `WAKE ${now} after ${offBy}ms, socket is open: ${isOpen}`;
        //     console.log(message);
        //     this.setHeartbeat();
        // } else {
        //     this.setHeartbeat();
        // }
    }

    heartbeatAction = () => {
        console.log('Reopen after disconnect from sleep.')
        this.makeSocket();
        this.heartbeatTimer = setTimeout(() => {
            if (!this.stop && this.api.parent.gameController &&
                this.webSocket.readyState === WebSocket.OPEN) {
                this.api.parent.gameController.reconnectToGame();
                this.setHeartbeat();
                // beep();
            } else {
                console.log('Cannot reconnect.');
                this.setHeartbeat();
                // beep(1);
                // beep(1);
            }
        }, 2000);
    }

}

function getTime() {
    return (new Date()).getTime();
}
